import * as React from 'react';
import * as ReactDOM from 'react-dom/client';
import tailwindUrl from 'tailwindcss/tailwind.css?url';
import appCssUrl from 'app.css?url';
import fontsCssUrl from 'theme/fonts.css?url';
import webComponentHostCssUrl from 'theme/web-component-host.css?url';
import { userSession } from 'state/session';
import WebComponentRoot, { IWebComponentContext } from 'components/WebComponentRoot';
import { getFavicons } from 'theme/favicons';
import { TFaviconConfig } from 'theme/favicons/types';
import { webComponentAssetUrl } from './ui/stage';
import { detectTheme } from './ui/browserTheme';
import { applyUserTheme } from './ui/applyUserTheme';
import { brandSchema } from 'data/schema';
import { installSyncTelemetryProbe } from './ui/syncTelemetryProbe';
import { notifyElementLifecycle } from './ui/app';
import { reattachDetachedStylesheets } from './ui/shadowStylesheets';
import { isRecorderAllowed } from 'telemetry/recorder/gate';
import { ensureDialogHost } from './dialogHost';

// Installed here because both deployment modes route through `wc()`: the standalone app via
// `web-components/App.ts` and the web-component bundle via `src/index.ts`. One import covers both.
installSyncTelemetryProbe();

// Session Flight Recorder (AVS-4426), installed from the same single point and for the same reason.
//
// Three things make this safe to run at module scope in a partner dealer portal:
//  - the runtime gate is checked FIRST and does not default, so `prod`, `pr`, `test`, an unset STAGE
//    and vitest all refuse here (`telemetry/recorder/gate.ts`);
//  - the import is dynamic, and `vite.config.ts` replaces its target with an empty stub outside
//    ['dev','prelive'], so no recorder code exists in any other artefact to import;
//  - the promise is isolated. A recorder that fails to load may cost telemetry; it may never cost
//    the app its boot.
if (isRecorderAllowed()) {
  void import('telemetry/recorder/recorderInstall')
    .then(({ installRecorder }) => installRecorder())
    .catch((error: unknown) => console.warn('[Recorder] install failed and was isolated', error));
}

export type IWcOptions = {
  shadow?: boolean;
};

/**
 * A utility function to create a Web Component from a React Functional Component.
 * This function returns a class that extends `HTMLElement` and renders the given React component
 * inside the Web Component's shadow DOM or light DOM based on the provided options.
 *
 * @param Component - The React Functional Component to be rendered inside the Web Component.
 * @param options - Configuration options for the Web Component.
 * @param options.shadow - A boolean indicating whether to use shadow DOM. Defaults to `true`.
 *
 * @returns A class that extends `HTMLElement` and renders the React component.
 *
 * @example
 * ```typescript
 * const MyComponent: React.FC = () => <div>Hello, World!</div>;
 * customElements.define('my-component', wc(MyComponent));
 * ```
 */
export function wc(Component: React.FC, { shadow }: IWcOptions = { shadow: true }) {
  return class WebComponent extends HTMLElement {
    private root: ReactDOM.Root | null = null;

    private unsubscribes: Array<() => void> = [];

    // Memoized on first mount: the child-node move below empties this.childNodes, so a
    // remount would otherwise recompute hasSlot as false forever. Not set in the
    // constructor — per spec, children may not exist yet when the constructor runs.
    private hasSlot?: boolean;

    constructor() {
      super();
      if (!shadow) {
        return;
      }
      this.attachShadow({ mode: 'open' });
    }

    connectedCallback() {
      // AVS-4433: reported BEFORE the early return, so a same-tick move is distinguishable from a
      // real mount in the recording — DCC's `__e3dReinitCallback` produces the second, not the
      // first. A no-op unless a lane has registered a sink, and isolated if one throws.
      notifyElementLifecycle('connected', this);

      // AVS-4487: a moveBefore() in Chromium <152 detaches our shadow-root stylesheets; re-attach them.
      // Relies on moveBefore() falling back to connectedCallback; a future connectedMoveCallback must call this too.
      if (this.shadowRoot) {
        reattachDetachedStylesheets(this.shadowRoot);
      }

      // Live root survives a same-tick move (or a reattach before the microtask teardown ran):
      // skip re-mounting and re-registering listeners so nothing stacks. Attributes changed
      // during that brief detached window are not re-read (they were never reactive post-mount).
      if (this.root) {
        return;
      }

      this.theme();
      const props = this.getPropsFromAttributes();
      const reactRootElement = shadow ? this.shadowRoot : this;
      if (this.hasSlot === undefined) {
        this.hasSlot = this.hasSlottedChildren();
      }
      this.root = ReactDOM.createRoot(reactRootElement!);

      this.root.render(
        <WebComponentRoot
          {...props}
          rootElement={shadow ? (reactRootElement as ShadowRoot) : undefined}
          webComponent={this as unknown as IWebComponentContext['webComponent']}
          hasSlot={this.hasSlot}
        >
          <Component />
        </WebComponentRoot>
      );

      if (shadow) {
        // move children to Shadow DOM to preserve Web Component styling
        this.shadowRoot?.append(...this.childNodes);
      }

      // AVS-4404: guarantees a connected, visible confirm-dialog claimant for the whole document.
      // Every integrator-placed host can be hidden at the moment a dialog opens; a claimed dialog
      // has no settle timer, so a dialog nobody can show would hang share() forever.
      ensureDialogHost();
    }

    disconnectedCallback() {
      // AVS-4433: reported synchronously, NOT inside the microtask below — the recorder wants the
      // detach as it happened, including the same-tick reshuffles the microtask deliberately
      // ignores, because a DCC remount looks exactly like one until the reattach fails to arrive.
      notifyElementLifecycle('disconnected', this);

      // Defer teardown one microtask so a same-tick host-DOM reshuffle is not a real detach.
      queueMicrotask(() => {
        if (this.isConnected) {
          return;
        }
        this.root?.unmount();
        this.root = null;
        this.unsubscribes.forEach((unsubscribe) => unsubscribe());
        this.unsubscribes = [];
      });
    }

    private getPropsFromAttributes<T = Record<string, unknown>>(): T {
      const props: Record<string, string> = {};

      for (let index = 0; index < this.attributes.length; index++) {
        const attribute = this.attributes[index];
        props[normalizeAttribute(attribute.name)] = attribute.value;
      }

      return props as T;
    }

    _initialDisplay: string = '';

    private hiddenForStyles = false;

    // Idempotent: a remount while the first stylesheets still load must not save our own 'none'
    hide() {
      if (!this.hiddenForStyles) {
        this._initialDisplay = this.style.display;
        this.hiddenForStyles = true;
      }
      this.style.display = 'none';
    }

    show() {
      if (this.hiddenForStyles) {
        this.style.display = this._initialDisplay;
        this.hiddenForStyles = false;
      }

      const webComponentName = this.tagName.toLowerCase();
      this.classList.add(`${webComponentName}-ready`);
    }

    /**
     * Injects a stylesheet into the shadow DOM.
     * @returns A promise that resolves when the stylesheet is loaded.
     */
    private addStyleSheet(url: string) {
      // Check if the stylesheet is already loaded
      if (this.shadowRoot?.querySelector(`link[href="${url}"]`)) {
        return Promise.resolve();
      }

      const linkElement = document.createElement('link');
      linkElement.rel = 'stylesheet';
      linkElement.href = url;
      this?.shadowRoot?.appendChild(linkElement);

      const loadingPromise = new Promise<void>((resolve, reject) => {
        linkElement.onload = () => {
          resolve();
        };
        linkElement.onerror = () => {
          reject(new Error(`Stylesheet failed to load: ${url}`));
        };
      });
      return loadingPromise;
    }

    theme() {
      // prevent FOUC while tailwind loads into the shadow DOM
      this.hide();

      // Inject the external stylesheet
      Promise.all([
        // tailwind
        this.addStyleSheet(webComponentAssetUrl(tailwindUrl)),
        // app css
        this.addStyleSheet(webComponentAssetUrl(appCssUrl)),
        // web component host css
        this.addStyleSheet(webComponentAssetUrl(webComponentHostCssUrl)),
      ])
        .then(() => {
          this.show();
        })
        .catch((error: unknown) => {
          // A failed stylesheet must not leave the element permanently hidden (hide() ran
          // before loading started) — show unstyled rather than not at all.
          console.warn('[wc] Stylesheet failed to load — showing unstyled', error);
          this.show();
        });

      // Check if the global style#e3d-theme exists and move it to the shadow root
      const globalStyle = document.querySelector('#e3d-theme');
      if (globalStyle) {
        this.shadowRoot?.appendChild(globalStyle.cloneNode(true));
      }

      this.applyBrandingCss();
      this.injectFonts();
    }

    private applyBrandingCss() {
      const brand = userSession().get('brand');

      if (brand) {
        this.classList.add(`brand-${brand}`);
        this.addFavicons(brand);
      }

      // Apply default customer class immediately. If it turns out the user is a dealer, we'll switch it after.
      this.classList.add('ex3d-customer');
      applyUserTheme();

      // Capture the unsubscribe so disconnectedCallback can tear the listener down (was leaked before)
      this.unsubscribes.push(
        userSession().on('change', (keys, newValues) => {
          const isBrandBeingChanged = keys.includes('brand');
          if (isBrandBeingChanged) {
            const brandIndex = keys.indexOf('brand');
            const newBrand = newValues[brandIndex];
            const inactiveBrands = brandSchema.options.values().filter((b) => b !== newBrand);
            // We do not reliably know the exact brand that was previously applied, so we remove all irrelevant brand classes
            inactiveBrands.forEach((brand) => {
              this.classList.remove(`brand-${brand}`);
            });
            this.classList.add(`brand-${newBrand}`);
            this.addFavicons(newBrand);
          }
        })
      );
    }

    private async addFavicons(brand: string) {
      try {
        const browserTheme = detectTheme();
        const { favicons } = await getFavicons(brand, browserTheme);

        const head = document.head || document.getElementsByTagName('head')[0] || document.createElement('head');
        if (!document.head) {
          document.documentElement.prepend(head);
        }

        favicons.forEach((favicon: TFaviconConfig) => {
          const searchAttributes = Object.keys(favicon).filter((key) => key !== 'href');

          const existingLink = Array.from(head.querySelectorAll('link')).find((existing) =>
            searchAttributes.every((key) => existing.getAttribute(key) === favicon[key as keyof TFaviconConfig])
          );

          if (existingLink) {
            head.removeChild(existingLink);
          }

          const link = document.createElement('link');
          Object.assign(link, favicon);
          head.appendChild(link);
        });
      } catch (error) {
        console.error('Failed to add favicons:', error);
      }
    }

    /**
     * Inject the fonts stylesheet into the document head so web component can use the fonts.
     */
    injectFonts() {
      const globalFontsStyle = document.querySelector('#e3d-fonts');
      if (globalFontsStyle) {
        // already added once, no need to re-apply
        return;
      }
      const fontsLinkElement = document.createElement('link');
      fontsLinkElement.id = 'e3d-fonts';
      fontsLinkElement.rel = 'stylesheet';
      fontsLinkElement.href = webComponentAssetUrl(fontsCssUrl);
      document.head.appendChild(fontsLinkElement);
    }

    private hasSlottedChildren() {
      return Array.from(this.childNodes).some(
        (node) => node.nodeType !== Node.TEXT_NODE || node.textContent?.trim() !== ''
      );
    }
  };
}

/**
 * Converts a kebab-case attribute name to camelCase.
 *
 * This function takes a string representing an attribute name in kebab-case
 * (e.g., "data-attribute-name") and converts it to camelCase (e.g., "dataAttributeName").
 *
 * @param attribute - The kebab-case attribute name to be normalized.
 * @returns The camelCase version of the attribute name.
 */
export function normalizeAttribute(attribute: string) {
  return attribute.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());
}
